import {AgentLicenseDto} from 'Agent/AgentInformation/api';
import {AsyncPage, Empty} from 'Core/models/Async';
import {DateRange} from 'Core/models/DateRange';
import {FilterOptionUnion, FilterOptionValue} from 'Core/models/FilterOption';
import {Page} from 'Core/models/Page';
import {constant} from 'fp-ts/lib/function';
import {fold as foldOption, fromNullable} from 'fp-ts/lib/Option';
import {pipe} from 'fp-ts/lib/pipeable';
import {createAction} from 'Shared/utilities/actionCreator';
import {PageFetchAction} from 'Shared/utilities/sagaApiGenerators';
import {ValidAgentNumber} from 'Users/models/AgentNumber';

export type FilterEnum = {
  name: string;
  value: string;
};

export enum AgentLicenseActionType {
  CHANGE_AGENT_LICENSE_FILTERS = 'CHANGE_AGENT_LICENSE_FILTERS',
  GET_AGENT_LICENSES = 'GET_AGENT_LICENSES',
  CHANGE_AGENT_LICENSES = 'CHANGE_AGENT_LICENSES',
}

export const changeAgentLicenseFilters = (filters: AgentLicenseFilters) =>
  createAction(AgentLicenseActionType.CHANGE_AGENT_LICENSE_FILTERS, {filters});
export const getAgentLicenses = (
  agentNumbers: ValidAgentNumber[],
  isNewYork: boolean,
  encryptedIdentifier: string,
  pageOption?: Page<AgentLicenseDto>,
  filter?: AgentLicenseFilters
) => {
  const payload = pipe(
    fromNullable(pageOption),
    foldOption(constant(PageFetchAction.Initial({filter})), page =>
      PageFetchAction.Additional({existingPage: page, filter})
    )
  );

  return createAction(AgentLicenseActionType.GET_AGENT_LICENSES, {
    agentNumbers,
    isNewYork,
    encryptedIdentifier,
    ...payload,
  });
};
export const changeAgentLicenses = (agentLicenses: AsyncPage<Page<AgentLicenseDto>>) =>
  createAction(AgentLicenseActionType.CHANGE_AGENT_LICENSES, agentLicenses);

const EmptyAgentLicenseFilter: AgentLicenseFilters = {
  isNewYork: false,
  agentName: '',
  agentNumber: '',
  managerName: '',
  managerNumber: '',
  licenseNumber: '',
  licenseState: '',
  status: FilterOptionValue.EmptyOption(),
  effectiveDate: {
    endDate: '',
    startDate: '',
  },
  expirationDate: {
    endDate: '',
    startDate: '',
  },
  life: FilterOptionValue.EmptyOption(),
  deferredAnnuity: FilterOptionValue.EmptyOption(),
  additional: FilterOptionValue.EmptyOption(),
  disability: FilterOptionValue.EmptyOption(),
  medical: FilterOptionValue.EmptyOption(),
  universalLife: FilterOptionValue.EmptyOption(),
};

export type AgentLicenseFilters = {
  agentName: string;
  agentNumber: string;
  managerName: string;
  managerNumber: string;
  licenseNumber: string;
  licenseState: string;
  effectiveDate: DateRange;
  expirationDate: DateRange;
  status: FilterOptionUnion;
  isNewYork: boolean;
  life: FilterOptionUnion;
  deferredAnnuity: FilterOptionUnion;
  additional: FilterOptionUnion;
  disability: FilterOptionUnion;
  medical: FilterOptionUnion;
  universalLife: FilterOptionUnion;
};

export type AgentLicenseState = {
  agentLicenses: AsyncPage<Page<AgentLicenseDto>>;
  filters: AgentLicenseFilters;
};

type AgentLicenseAction = ReturnType<typeof changeAgentLicenseFilters> | ReturnType<typeof changeAgentLicenses>;

const DEFAULT_STATE = {
  agentLicenses: new Empty(),
  filters: EmptyAgentLicenseFilter,
};

export default (state: AgentLicenseState = DEFAULT_STATE, action: AgentLicenseAction): AgentLicenseState => {
  switch (action.type) {
    case AgentLicenseActionType.CHANGE_AGENT_LICENSE_FILTERS:
      return {
        ...state,
        filters: action.payload.filters,
      };
    case AgentLicenseActionType.CHANGE_AGENT_LICENSES:
      return {
        ...state,
        agentLicenses: action.payload,
      };
    default:
      return state;
  }
};
